import { Link } from 'react-router-dom';
import type { Topic } from '@/mocks/topics';
import { formatCount, statusStyle } from '@/utils/format';

interface TopicHeaderProps {
  topic: Topic;
}

export default function TopicHeader({ topic }: TopicHeaderProps) {
  const style = statusStyle(topic.status);

  return (
    <section className="bg-background-50">
      <div className="w-full px-4 pt-10 md:px-8 md:pt-12">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/topics?category=${topic.categoryId}`}
            className="bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {topic.categoryName}
          </Link>
          <span className={`flex items-center gap-1.5 px-2.5 py-1 text-xs ${style.badge}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
            {topic.status}
          </span>
        </div>

        <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
          {topic.title}
        </h1>

        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {topic.summary}
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" aria-hidden="true" />
            {topic.editor}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true" />
            更新于 {topic.updatedAt}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-fire-line text-primary-500" aria-hidden="true" />
            热度 {formatCount(topic.heat)}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-chat-3-line" aria-hidden="true" />
            讨论 {formatCount(topic.comments)}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" aria-hidden="true" />
            浏览 {formatCount(topic.views)}
          </span>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {topic.tags.map((tag) => (
            <Link
              key={tag}
              to={`/topics?q=${encodeURIComponent(tag)}`}
              className="border border-background-300 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-8 w-full px-4 md:px-8">
        <div className="h-56 w-full overflow-hidden bg-background-200 md:h-80">
          <img
            src={topic.cover}
            alt={`${topic.title} 话题配图`}
            title={`${topic.title} - ${topic.categoryName}`}
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    </section>
  );
}